import type {
  ApiRequestBody,
  ApiResponseBody,
  ApiSearchQuery,
} from '@/lib/api/api-type-helper';

// ---------------------------------------------------------------------------
// List API Types
// ---------------------------------------------------------------------------

export type GetTestSetListQuery = ApiSearchQuery<
  '/v2/qa-force/test-sets',
  'get'
>;
export type GetTestSetListResponse = ApiResponseBody<
  '/v2/qa-force/test-sets',
  'get'
>;
export type TestSetListItem = GetTestSetListResponse['items'][0];

export type TestSetOrderBy = NonNullable<GetTestSetListQuery>['orderBy'];

// ---------------------------------------------------------------------------
// Count API Types
// ---------------------------------------------------------------------------

export type GetTestSetCountQuery = ApiSearchQuery<
  '/v2/qa-force/test-sets/count',
  'get'
>;
export type GetTestSetCountResponse = ApiResponseBody<
  '/v2/qa-force/test-sets/count',
  'get'
>;

// ---------------------------------------------------------------------------
// Detail API Types
// ---------------------------------------------------------------------------

export type GetTestSetResponse = ApiResponseBody<
  '/v2/qa-force/test-sets/{id}',
  'get'
>;

/** TestSet 詳細に紐づく関連開発チケット(TestSetTicket → Ticket、ord 順)。 */
export type RelatedTicketItem = GetTestSetResponse['relatedTickets'][number];

/**
 * TestSet 部分更新 PATCH リクエスト。
 *
 * BE: `PATCH /v2/qa-force/test-sets/{id}`。全項目 optional。
 * N:M フィールド(`relatedTicketIds` 等)は ID 配列で replace-all 同期される。
 * 生成型に `id` が必須として含まれるため、呼び出し側で path id を body にも詰める。
 */
export type PatchTestSetRequest = ApiRequestBody<
  '/v2/qa-force/test-sets/{id}',
  'patch'
>;

/** TestSet PATCH レスポンス(BE は更新した id 等のみ返却)。 */
export type PatchTestSetResponse = ApiResponseBody<
  '/v2/qa-force/test-sets/{id}',
  'patch'
>;

/** designScopes に渡せる TestSetScope enum 値。 */
export const TestSetScope = {
  NORMAL: 'NORMAL',
  SEMI_NORMAL: 'SEMI_NORMAL',
  ABNORMAL: 'ABNORMAL',
  OTHER: 'OTHER',
} as const;
export type TestSetScope = (typeof TestSetScope)[keyof typeof TestSetScope];

const TEST_SET_SCOPE_VALUES: ReadonlySet<string> = new Set(
  Object.values(TestSetScope),
);
export function isTestSetScope(
  value: string | null | undefined,
): value is TestSetScope {
  return value != null && TEST_SET_SCOPE_VALUES.has(value);
}

// ---------------------------------------------------------------------------
// TestSet Status（6 値）
//
// 2026-05-05 の status enum 再設計後:
//   DESIGNING / IMPLEMENTING / READY / EXECUTED / APPROVED / NOT_EXECUTED
// テストセットタブのフィルタタブには上記 6 値 + ALL を表示する。
// ---------------------------------------------------------------------------

export const TestSetStatus = {
  DESIGNING: 'DESIGNING',
  IMPLEMENTING: 'IMPLEMENTING',
  READY: 'READY',
  EXECUTED: 'EXECUTED',
  APPROVED: 'APPROVED',
  NOT_EXECUTED: 'NOT_EXECUTED',
} as const;
export type TestSetStatus = (typeof TestSetStatus)[keyof typeof TestSetStatus];

const TEST_SET_STATUS_VALUES: ReadonlySet<string> = new Set(
  Object.values(TestSetStatus),
);
export function isTestSetStatus(
  value: string | null | undefined,
): value is TestSetStatus {
  return value != null && TEST_SET_STATUS_VALUES.has(value);
}

/** 一覧画面のステータスフィルタ用。ドメイン値 + "ALL"。 */
export type TestSetStatusFilter = TestSetStatus | 'ALL';
